/**
 * CubePanel — crafting / synthesis + selling (and a trading teaser).
 *
 * CUBO tab: 3×3 grid. Place 9 items of the same rarity → 1 item of next tier.
 * VENDER tab: burn items for gold.
 * TROCAR tab: "coming soon" placeholder until online P2P trading ships.
 *
 * @module ui/panels/CubePanel
 */

import { BasePanel } from './BasePanel.js';
import { rollEquipment } from '../../core/items.js';
import { Rng } from '../../core/rng.js';
import { confirmModal } from '../helpers/confirmModal.js';

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];

// Base gold paid by the system when an item is burned, by rarity. Scales up
// with item level and affix count so a juicy drop is worth more than vendor trash.
const SELL_BASE = { common: 5, uncommon: 14, rare: 38, epic: 95, legendary: 240, mythic: 600 };

function iconImg(iconKey, size = 24) {
  const key = iconKey ?? 'sword';
  return `<img src="assets/sprites/items/icon_${key}.png" width="${size}" height="${size}" style="image-rendering:pixelated;display:block" alt="${key}" onerror="this.onerror=null;this.src='assets/sprites/items/icon_sword.png'">`;
}

export class CubePanel extends BasePanel {
  constructor(opts) {
    super({ name: 'cube', title: 'CUBO', state: opts.state, data: opts.data, hooks: opts.hooks });
    this._gridSlots = new Array(9).fill(null);
    this._tab = 'craft';
  }

  render() {
    const tabBtn = (id, label) =>
      `<button class="pd-btn pd-btn-${this._tab === id ? 'primary' : 'secondary'} pd-tab-${id}"
               style="flex:1;font-size:11px">${label}</button>`;
    this.body.innerHTML = `
      <div style="display:flex;gap:6px;margin-bottom:12px;flex-wrap:wrap">
        ${tabBtn('craft', '⬡ Sintetizar')}
        ${tabBtn('sell', '⛁ Vender')}
        ${tabBtn('trade', '⇄ Trocar')}
      </div>
      <div id="pd-cube-tab-content"></div>
    `;
    this.body.querySelector('.pd-tab-craft').addEventListener('click', () => { this._tab = 'craft'; this.render(); });
    this.body.querySelector('.pd-tab-sell').addEventListener('click', () => { this._tab = 'sell'; this.render(); });
    this.body.querySelector('.pd-tab-trade').addEventListener('click', () => { this._tab = 'trade'; this.render(); });

    if (this._tab === 'craft') this._renderCraft();
    else if (this._tab === 'sell') this._renderSell();
    else this._renderTrade();
  }

  // ─── SELL ─────────────────────────────────────────────────────────────────

  /** Gold the system pays for an item: base by rarity, scaled by level + affixes. */
  _sellPrice(item) {
    const base = SELL_BASE[item.rarity] ?? 5;
    const lvlMult = 1 + 0.15 * Math.max(0, (item.itemLevel ?? 1) - 1);
    const affixMult = 1 + 0.1 * (item.affixes?.length ?? 0);
    return Math.max(1, Math.round(base * lvlMult * affixMult));
  }

  _renderSell() {
    const tab = this.body.querySelector('#pd-cube-tab-content');
    const items = this.state.inventory;
    const commons = items.filter((x) => x.rarity === 'common');
    const commonsTotal = commons.reduce((s, x) => s + this._sellPrice(x), 0);

    tab.innerHTML = `
      <div style="text-align:center;margin-bottom:10px;color:var(--pd-muted);font-size:11px">
        Queime um item para receber <strong style="color:var(--pd-gold)">ouro</strong> do sistema.
        <span style="display:block;font-size:10px;margin-top:2px">Esta ação é permanente.</span>
      </div>
      <div style="text-align:center;margin-bottom:10px;font-size:12px">
        Ouro atual: <strong style="color:var(--pd-gold)">⛁ ${this.state.player.gold}</strong>
      </div>
      ${commons.length > 1 ? `
      <div style="text-align:center;margin-bottom:10px">
        <button class="pd-btn pd-btn-secondary pd-sell-bulk-common" style="font-size:11px">
          🔥 Vender todos os Comuns (${commons.length}) — ⛁ ${commonsTotal}
        </button>
      </div>` : ''}
      <div class="pd-section-title">Mochila — clique para vender</div>
      <div class="pd-bag-grid" id="pd-sell-picker"></div>
    `;

    tab.querySelector('.pd-sell-bulk-common')?.addEventListener('click', () => this._sellBulk('common'));

    const grid = tab.querySelector('#pd-sell-picker');
    if (!items.length) {
      grid.innerHTML = '<p class="pd-empty">Mochila vazia. Derrote monstros para dropar itens.</p>';
      return;
    }

    const order = ['mythic', 'legendary', 'epic', 'rare', 'uncommon', 'common'];
    const sorted = [...items].sort((a, b) => order.indexOf(a.rarity) - order.indexOf(b.rarity));
    for (const item of sorted) {
      const color = this.data.rarity?.[item.rarity]?.color ?? '#9d9d9d';
      const price = this._sellPrice(item);
      const cell = document.createElement('button');
      cell.className = 'pd-bag-cell';
      cell.style.borderColor = color;
      cell.style.position = 'relative';
      cell.innerHTML = `${iconImg(item.icon, 28)}
        <span style="position:absolute;bottom:-2px;right:1px;font-size:9px;color:var(--pd-gold);
                     text-shadow:0 1px 2px #000;font-weight:700">${price}</span>`;
      cell.title = `${item.label} · ${this.data.rarity?.[item.rarity]?.label ?? item.rarity}\nVender por ⛁ ${price}`;
      cell.addEventListener('click', () => this._sellItem(item.id));
      grid.appendChild(cell);
    }
  }

  async _sellItem(itemId) {
    let idx = this.state.inventory.findIndex((x) => x.id === itemId);
    if (idx < 0) return;
    const item = this.state.inventory[idx];
    const price = this._sellPrice(item);
    const rLabel = this.data.rarity?.[item.rarity]?.label ?? item.rarity;
    // In-game modal: native confirm() is blocked inside the PiP window.
    const ok = await confirmModal(this.root.ownerDocument, {
      icon: '🔥',
      title: `Vender "${item.label}"?`,
      message: `${rLabel} · vale <strong style="color:var(--pd-gold)">⛁ ${price}</strong><br>O item será destruído permanentemente.`,
      confirmLabel: 'Vender',
    });
    if (!ok) return;
    // Re-find: the modal is async and combat kept dropping items meanwhile.
    idx = this.state.inventory.findIndex((x) => x.id === itemId);
    if (idx < 0) return;

    this.state.inventory.splice(idx, 1);
    this.state.player.gold += price;
    this.hooks.onChange?.();
    this.render();

    const msg = document.createElement('div');
    msg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--pd-surface);border:2px solid var(--pd-gold);border-radius:12px;padding:18px 26px;text-align:center;z-index:9999;animation:fadeIn .2s';
    msg.innerHTML = `<div style="font-size:1.3rem;margin-bottom:6px">⛁ +${price} ouro</div>
      <div style="color:var(--pd-muted);font-size:11px">${item.label} foi queimado.</div>`;
    this.root.ownerDocument.body.appendChild(msg);
    setTimeout(() => msg.remove(), 1600);
  }

  /** Burn every item of a given rarity in one confirm instead of one-by-one. */
  async _sellBulk(rarity) {
    let batch = this.state.inventory.filter((x) => x.rarity === rarity);
    if (!batch.length) return;
    let total = batch.reduce((s, x) => s + this._sellPrice(x), 0);
    const rLabel = this.data.rarity?.[rarity]?.label ?? rarity;
    // In-game modal: native confirm() is blocked inside the PiP window.
    const ok = await confirmModal(this.root.ownerDocument, {
      icon: '🔥',
      title: `Vender ${batch.length} itens ${rLabel}?`,
      message: `Total: <strong style="color:var(--pd-gold)">⛁ ${total}</strong><br>Todos serão destruídos permanentemente.`,
      confirmLabel: 'Vender tudo',
    });
    if (!ok) return;
    // Re-collect: the modal is async and combat kept dropping items meanwhile.
    batch = this.state.inventory.filter((x) => x.rarity === rarity);
    if (!batch.length) return;
    total = batch.reduce((s, x) => s + this._sellPrice(x), 0);

    const ids = new Set(batch.map((x) => x.id));
    this.state.inventory = this.state.inventory.filter((x) => !ids.has(x.id));
    this.state.player.gold += total;
    this.hooks.onChange?.();
    this.render();

    const msg = document.createElement('div');
    msg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--pd-surface);border:2px solid var(--pd-gold);border-radius:12px;padding:18px 26px;text-align:center;z-index:9999;animation:fadeIn .2s';
    msg.innerHTML = `<div style="font-size:1.3rem;margin-bottom:6px">⛁ +${total} ouro</div>
      <div style="color:var(--pd-muted);font-size:11px">${batch.length} itens ${rLabel} foram queimados.</div>`;
    this.root.ownerDocument.body.appendChild(msg);
    setTimeout(() => msg.remove(), 1600);
  }

  // ─── CRAFT ────────────────────────────────────────────────────────────────

  _renderCraft() {
    const items = this.state.inventory;
    const gridFilled = this._gridSlots.filter(Boolean).length;
    const canCraft = gridFilled === 9 && this._validateRarity();
    const tab = this.body.querySelector('#pd-cube-tab-content');

    tab.innerHTML = `
      <div style="text-align:center;margin-bottom:10px;color:var(--pd-muted);font-size:11px">
        Combine <strong>9 itens</strong> da mesma raridade para forjar um do tier superior.
      </div>
      <div class="pd-cube-grid" style="
        display:grid;grid-template-columns:repeat(3,1fr);gap:6px;
        max-width:210px;margin:0 auto 10px;
      ">
        ${this._gridSlots.map((id, i) => this._craftSlotHTML(i, id, items)).join('')}
      </div>
      <div style="text-align:center;margin-bottom:10px;font-size:11px;color:var(--pd-muted)">
        ${gridFilled}/9 itens
        ${gridFilled === 9 ? `<span style="color:${canCraft ? 'var(--pd-rarity-uncommon)' : '#e14b4b'};display:block">
          ${canCraft ? '✓ Prontos para sintetizar!' : '✗ Todos devem ter a MESMA raridade'}
        </span>` : ''}
      </div>
      <div class="pd-section-title">Mochila</div>
      <div class="pd-bag-grid" id="pd-craft-picker"></div>
      <div style="text-align:center;margin-top:10px;display:flex;gap:6px;justify-content:center">
        <button class="pd-btn pd-btn-primary pd-cube-craft" style="font-size:.95rem;padding:8px 24px" ${!canCraft ? 'disabled' : ''}>
          ⬡ Sintetizar
        </button>
        <button class="pd-btn pd-btn-secondary pd-cube-clear" style="font-size:11px">Limpar</button>
      </div>
    `;

    this._renderCraftPicker(items);
    tab.querySelector('.pd-cube-craft')?.addEventListener('click', () => this._craft());
    tab.querySelector('.pd-cube-clear')?.addEventListener('click', () => { this._gridSlots = new Array(9).fill(null); this.render(); });
  }

  _craftSlotHTML(i, itemId, items) {
    const item = itemId ? items.find((x) => x.id === itemId) : null;
    const border = item ? (this.data.rarity?.[item.rarity]?.color ?? '#4a3a26') : 'var(--pd-border)';
    const icon = item ? iconImg(item.icon) : '<span style="color:var(--pd-muted);font-size:16px">·</span>';
    return `<button class="pd-cube-slot" data-slot="${i}"
      style="aspect-ratio:1;background:var(--pd-surface);border:2px solid ${border};border-radius:8px;
             cursor:pointer;display:flex;align-items:center;justify-content:center;transition:all var(--pd-transition)"
      title="${item ? `${item.label} (${item.rarity}) — clique para remover` : 'Vazio'}"
    >${icon}</button>`;
  }

  _renderCraftPicker(items) {
    const grid = this.body.querySelector('#pd-craft-picker');
    if (!grid) return;
    const available = items.filter((x) => !this._gridSlots.includes(x.id));
    if (!available.length) {
      grid.innerHTML = '<p class="pd-empty">Mochila vazia.</p>';
    } else {
      for (const item of available) {
        const color = this.data.rarity?.[item.rarity]?.color ?? '#9d9d9d';
        const cell = document.createElement('button');
        cell.className = 'pd-bag-cell';
        cell.style.borderColor = color;
        cell.innerHTML = iconImg(item.icon, 28);
        cell.title = `${item.label} · ${item.rarity}\n(clique para adicionar ao cubo)`;
        cell.addEventListener('click', () => {
          const idx = this._gridSlots.indexOf(null);
          if (idx >= 0) { this._gridSlots[idx] = item.id; this.render(); }
        });
        grid.appendChild(cell);
      }
    }
    // Wire remove on craft slots
    this.body.querySelectorAll('.pd-cube-slot').forEach((btn) => {
      btn.addEventListener('click', () => {
        const i = parseInt(btn.dataset.slot);
        if (this._gridSlots[i]) { this._gridSlots[i] = null; this.render(); }
      });
    });
  }

  _validateRarity() {
    const ids = this._gridSlots.filter(Boolean);
    if (ids.length !== 9) return false;
    const rarities = ids.map((id) => this.state.inventory.find((x) => x.id === id)?.rarity).filter(Boolean);
    return rarities.length === 9 && rarities.every((r) => r === rarities[0]);
  }

  _craft() {
    if (!this._validateRarity()) return;
    const items = this.state.inventory;
    const gridItems = this._gridSlots.map((id) => items.find((x) => x.id === id)).filter(Boolean);
    const rarity = gridItems[0].rarity;
    const nextIdx = RARITY_ORDER.indexOf(rarity) + 1;
    if (nextIdx >= RARITY_ORDER.length) { this._cubeToast('Já é Mítico — não pode subir mais!'); return; }
    const nextRarity = RARITY_ORDER[nextIdx];

    // Remove the 9 consumed items
    for (const item of gridItems) {
      const i = this.state.inventory.findIndex((x) => x.id === item.id);
      if (i >= 0) this.state.inventory.splice(i, 1);
    }

    // Pick a random valid item type from items catalog (excluding _doc)
    const typeIds = Object.keys(this.data.items ?? {}).filter((k) => k !== '_doc' && this.data.items[k].slot);
    const typeId = typeIds[Math.floor(Math.random() * typeIds.length)];
    const newItem = rollEquipment(typeId, nextRarity, gridItems[0].itemLevel + 1, this.data, new Rng());
    this.state.inventory.push(newItem);

    this._gridSlots = new Array(9).fill(null);
    this.hooks.onChange?.();
    this.render();

    // Flash success
    const msg = document.createElement('div');
    msg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--pd-surface);border:2px solid var(--pd-gold);border-radius:12px;padding:20px 28px;text-align:center;z-index:9999;animation:fadeIn .2s';
    msg.innerHTML = `<div style="font-size:1.4rem;margin-bottom:8px">✨ Sintetizado!</div>
      <div style="color:${this.data.rarity?.[nextRarity]?.color ?? '#fff'};font-weight:bold">${newItem.label}</div>
      <div style="color:var(--pd-muted);font-size:11px;margin-top:4px">${nextRarity}</div>`;
    this.root.ownerDocument.body.appendChild(msg);
    setTimeout(() => msg.remove(), 2200);
  }

  /** Brief in-game notice (native alert() is blocked inside the PiP window). */
  _cubeToast(text) {
    const doc = this.root.ownerDocument;
    const msg = doc.createElement('div');
    msg.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:var(--pd-surface);border:2px solid #e14b4b;border-radius:12px;padding:16px 24px;text-align:center;z-index:9999;animation:fadeIn .2s';
    msg.innerHTML = `<div style="font-size:1rem;color:var(--pd-text)">${text}</div>`;
    doc.body.appendChild(msg);
    setTimeout(() => msg.remove(), 1600);
  }

  // ─── TRADE ────────────────────────────────────────────────────────────────
  // Real player-to-player trading (online) is planned but not built yet. The
  // old tab faked a two-sided local trade that moved nothing — dishonest with
  // testers. Until the P2P system ships, this is an explicit "coming soon".

  _renderTrade() {
    const tab = this.body.querySelector('#pd-cube-tab-content');
    tab.innerHTML = `
      <div style="text-align:center;padding:26px 14px">
        <div style="font-size:2rem;margin-bottom:8px">⇄</div>
        <div style="font-size:14px;font-weight:800;color:var(--pd-chrome-text);margin-bottom:6px">
          Troca entre Jogadores
        </div>
        <div style="font-size:11px;color:var(--pd-muted);line-height:1.5;max-width:260px;margin:0 auto 14px">
          Em breve você vai poder trocar itens com outros jogadores online.
          Cada item tem identidade única, então nada se perde na troca.
        </div>
        <span class="pd-pill" style="border-color:var(--pd-gold);color:var(--pd-gold)">🔒 Em breve</span>
      </div>`;
  }
}
